import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles';
import { AtlChatPanelProps } from './messaging.props';
import { ButtonEnums } from '../button';
import '../dropdown';

import * as TagNames from './messaging.tagnames';
import { ChatFavoriteEvent, ChatSentEvent } from './messaging.events';
import { AtlButtonTemplate } from '../button/button.component';

@customElement(TagNames.ChatPanel)
export class AtlChatPanel extends LitElement implements AtlChatPanelProps {
   declare panelTitle: string;
   declare showFavoriteActionButton?: boolean;
   declare bodyClassName?: string;
   declare hideFooter?: boolean;
   declare chatInputBoxClassName?: string;
   declare chatInputBoxName?: string;
   declare chatInputBoxPlaceholder?: string;
   declare keepChatInputBoxValueAfterSubmit?: boolean;
   declare submitOnInputBoxEnter?: boolean;

   declare _chatInputBoxValue: string;

   static properties = {
      panelTitle: { attribute: 'panel-title', type: String },
      showFavoriteActionButton: {
         attribute: 'show-favorite-action-button',
         type: Boolean,
      },
      bodyClassName: { attribute: 'body-class-name', type: String },
      hideFooter: { attribute: 'hide-footer', type: Boolean },
      chatInputBoxClassName: {
         attribute: 'chat-input-box-class-name',
         type: String,
      },
      chatInputBoxName: { attribute: 'chat-input-box-name', type: String },
      chatInputBoxPlaceholder: {
         attribute: 'chat-input-box-placeholder',
         type: String,
      },
      keepChatInputBoxValueAfterSubmit: {
         attribute: 'keep-chat-input-box-value-after-submit',
         type: Boolean,
      },
      submitOnInputBoxEnter: {
         attribute: 'submit-on-input-box-enter',
         type: Boolean,
      },
      _chatInputBoxValue: { type: String, state: true },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         :host {
            display: block;
         }

         ::slotted([slot='slotMessagingBody']) {
            display: flex;
            flex-direction: column;
            gap: 1rem;
         }

         atlantis-dropdown {
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
         }

         atlantis-dropdown::part(dropdown),
         atlantis-dropdown::part(button) {
            height: 100%;
         }

         atlantis-dropdown::part(button) {
            font-size: 20px;
            color: #a6a6a6;
            transition: color 0.3s ease;
            padding-left: 16px;
            padding-right: 16px;
         }
      `,
   ];

   constructor() {
      super();
      this._chatInputBoxValue = '';
   }

   private _renderChatAction = () => {
      if (!this.showFavoriteActionButton) return nothing;

      return AtlButtonTemplate({
         variant: ButtonEnums.ButtonVariant.Action,
         buttonAriaSettings: {
            ariaLabel: 'Favourite Button',
            role: 'link',
         },
         iconName: 'star',
         onClick: () => {
            this.dispatchEvent(new ChatFavoriteEvent());
         },
      });
   };

   private _renderChatOptions = () => {
      return html`<atlantis-dropdown
         toggle-icon-name="ellipsis-vertical"
         variant=${ButtonEnums.ButtonVariant.Action}
         toggle-aria-label="Chat Options"
      >
         <slot name="actionMenu"></slot>
      </atlantis-dropdown>`;
   };

   renderChatFooter() {
      if (this.hideFooter) return nothing;

      const classNames = ['chat-input-box'];
      if (this.chatInputBoxClassName != null) {
         classNames.push(this.chatInputBoxClassName);
      }
      const name = this.chatInputBoxName ?? 'messagingChatMessage';
      const placeholder = this.chatInputBoxPlaceholder ?? 'Type your message';

      return html`
            <div class="messaging-chat-panel-footer">
                <input 
                    type="text"
                    class=${classNames.join(' ')}
                    name=${name}
                    placeholder=${placeholder}
                    .value=${this._chatInputBoxValue}
                    @input=${this._handleInputChange}
                    @keypress=${this._handleKeyPress}                  
               >
                </input>
                <div class="footer-buttons">
                    <div class="optional-buttons">
                        <slot name="slotFooterOptionalButtons"></slot>
                    </div>
                    <div class="submit-buttons">
                        <slot name="slotFooterAdditionalActionButtons"></slot>
                        ${AtlButtonTemplate({
                           variant: ButtonEnums.ButtonVariant.Action,
                           buttonAriaSettings: {
                              ariaLabel: 'Submit Button',
                              role: 'link',
                           },
                           iconName: 'paper-plane',
                           buttonClasses: 'footer-button-icon',
                           disabled: !this._chatInputBoxValue,
                           onClick: this._onSentClick,
                        })}                        
                    </div>
                </div>
            </div>`;
   }

   render() {
      const classNames = ['messaging-chat-panel-body'];
      if (this.bodyClassName != null) {
         classNames.push(this.bodyClassName);
      }
      return html` <div class="messaging-chat-panel">
         <div class="messaging-chat-panel-header">
            <div class="h5 chat-panel-header-title">${this.panelTitle}</div>
            <div class="chat-panel-header-action-buttons">
               ${this._renderChatAction()} ${this._renderChatOptions()}
            </div>
         </div>
         <div class=${classNames.join(' ')}>
            <slot name="slotMessagingBody"></slot>
         </div>
         ${this.renderChatFooter()}
      </div>`;
   }

   private _handleInputChange = (e: Event) => {
      const input = e.target as HTMLInputElement;
      this._chatInputBoxValue = input.value;
   };

   private _handleKeyPress = (event: KeyboardEvent) => {
      if (!this.submitOnInputBoxEnter) return;

      if (event.code.toLowerCase() === 'enter' && this._chatInputBoxValue) {
         this._onSentClick();
      }
   };

   private _onSentClick = () => {
      this.dispatchEvent(new ChatSentEvent(this._chatInputBoxValue));

      if (!this.keepChatInputBoxValueAfterSubmit) {
         this._chatInputBoxValue = '';
      }
   };
}
